import { filter, find, flatMap, forEach, get, includes, isEmpty, map, uniq , lowerCase , sortBy , groupBy} from "lodash";
import "./programPage.scss";
import Icon from "@assets/icon_config";
import { useEffect, useState , useMemo} from "react";
import { caseLessComparision, formatDisplayDate, getClientName } from "@utils/helpers";
import { EQUALS, NOT_EQUALS, dateFormatConfig, negativeOutcomeOfOutreach, positiveOutComeOfOutreach } from "@utils/constants";
import { ActionDateTime, SingleDate } from "@pages/member-details/care-coordination/constants";
import moment from "moment";
import { CPSRNotes as GoalNotes } from "../patientNotes/patientNotes";
import DataNotFound from "@components/noDataFound";
import { NO_DATA_FOUND } from "../../../../../../constants/constants";

const ProgramPage = ({ data, rules }: any) => {
  return (
    <div className="ui-care-plan-report__program">
      {(!isEmpty(data?.programInformation?.descriptionInfo) || !isEmpty(data?.programInformation?.info)) && <TitleValue data={data.programInformation} heading="Program Information" />}
      <div className="ui-care-plan-report__program__details">
        {/* left section */}
        <div className="ui-care-plan-report__program__details__left">
          <Goals data={data.goals} styleClass={"ui-care-plan-report__program__details__left__goal-careTeam-container__goals"} goalNotes={data.goalNotes} />

          <Tasks data={data} rules={rules} />
        </div>
      </div>
    </div>
  );
};

export const TitleValue = ({ data, heading }: any) => {
  return (
    <div className="ui-care-plan-report__program__title-value">
      <div className="ui-care-plan-report__program__title-value__heading">{heading}</div>
      <div className="ui-care-plan-report__program__title-value__info">
        {isEmpty(data) ? <DataNotFound message={NO_DATA_FOUND}/> : <></>}
        {map(data?.info, (item, index) => {
          return (
            item && (
              <div className="ui-care-plan-report__program__title-value__info__detail" key={index}>
                <div className="ui-care-plan-report__program__title-value__info__detail__label">{item.label}</div>
                <div className="ui-care-plan-report__program__title-value__info__detail__value">
                  {item.type === "INFO-DATETIME"
                    ? !isEmpty(item.value) && item.value !== "--"
                      ? formatDisplayDate(item.value, "reminderDateActualFormat", "defaultDateFormatWithTime", true)
                      : item.value
                    : item.label === "DOB" || item.label === "Last ER Admission" || item.label === "Previous Admissions" || item.label === "Previous Discharge" || item.type === "INFO-DATE"
                    ? !isEmpty(item.value) && item.value !== "--"
                      ? formatDisplayDate(item.value, "createDateFormat", "")
                      : item.value
                    : item.value}
                </div>
              </div>
            )
          );
        })}
      </div>

      <div className="ui-care-plan-report__program__title-value__detail-info">
        {map(data?.descriptionInfo, (item) => {
          return (
            <div className="ui-care-plan-report__program__title-value__detail-info__detail" key={`${item?.label?.trim()}`}>
              <div className="ui-care-plan-report__program__title-value__detail-info__detail__label">{item.label}</div>
              <div className="ui-care-plan-report__program__title-value__detail-info__detail__value">{item.value}</div>
            </div>
          );
        })}
      </div>
      <hr className="ui-care-plan-report__division"></hr>
    </div>
  );
};

export const ReferralsBody = ({ data }: any) => {
  return (
    <div className="ui-care-plan-report__program__title-value__info__half">
      {map(data, (item, index) => {
        return (
          item && (
            <div className="ui-care-plan-report__program__title-value__info__detail" key={index}>
              <div className="ui-care-plan-report__program__title-value__info__detail__label">{item.label}</div>
              <div className="ui-care-plan-report__program__title-value__info__detail__value">{item.value}</div>
            </div>
          )
        );
      })}
    </div>
  );
};

export const Referrals = ({ data, heading }: any) => {
  const midpoint = Math.ceil(data.length / 2);

  const firstHalf = data.filter((_: any, index: number) => index < midpoint);
  const secondHalf = data.filter((_: any, index: number) => index >= midpoint);

  return (
    <div className="ui-care-plan-report__program__title-value">
      <div className="ui-care-plan-report__program__title-value__heading">{heading}</div>
      <div className="ui-care-plan-report__program__title-value__info">
        {isEmpty(data) ? <DataNotFound message={NO_DATA_FOUND}/> : <></>}
       <ReferralsBody data={firstHalf}/>
       <ReferralsBody data={secondHalf}/>
      </div>
      <hr className="ui-care-plan-report__division"></hr>
    </div>
  );
}; 

const Goals = ({ data, styleClass, goalNotes }: any) => {
  return (
    <div className={`ui-care-plan-report__program__goals ${styleClass ? styleClass : ""}`}>
      <div className="ui-care-plan-report__program__goals__heading">Goals</div>

      {isEmpty(data) ? <DataNotFound message={NO_DATA_FOUND}/> : <></>}
      {map(data, (item, index) => {
        return (
          <div className="ui-care-plan-report__program__goals__detail" key={`goal${index}`}>
            <div className="ui-care-plan-report__program__goals__detail__status">{item.status === "Complete" ? <Icon name="tick-filled" /> : <Icon name="tick-empty" />}</div>
            <div className="ui-care-plan-report__program__goals__detail__description" dangerouslySetInnerHTML={{ __html: item.description }}></div>
          </div>
        );
      })}
      {goalNotes?.length > 0 ? <GoalNotes data={{ title: "Notes:", notes: goalNotes }} /> : null}
    </div>
  );
};

const Tasks = ({ data, rules }: any) => {
  return (
    <div className="ui-care-plan-report__program__tasks">
      <div className="ui-care-plan-report__program__tasks__heading">Interventions/Tasks</div>
      <div className="ui-care-plan-report__program__tasks__header">
        {/* <div className="ui-care-plan-report__program__tasks__header__td">NO</div> */}
        <div className="ui-care-plan-report__program__tasks__header__td ui-care-plan-report__program__tasks__header__td--description">DESCRIPTION</div>
        <div className="ui-care-plan-report__program__tasks__header__td">STATUS</div>
      </div>

      {isEmpty(data?.tasks) ? <DataNotFound message={NO_DATA_FOUND}/> : <></>}
      {map(data?.tasks, (item, index) => {
        const taskNotes = data.programNotes && data?.programNotes?.filter((note: any) => note?.taskId === item?.taskId);
        return (
          <div className="ui-care-plan-report__program__tasks__task-container">
            <SubTask taskItem={item} rules={rules} key={index} />
            {taskNotes && taskNotes?.length > 0 && <ProgramNotes data={taskNotes} />}
          </div>
        );
      })}
    </div>
  );
};

const SubTask = ({ taskItem, rules }: any) => {
  const [dependantSubTasks, setDependantSubTasks] = useState<any[]>([]);

  useEffect(() => {
    let dependantItems: any = [];

    forEach(taskItem.subtasks, (task) => {
      let dependents: any[] = [];

      const rule: any = [];
      forEach(task?.ruleName, (item: any) => {
        const temp_rule = find(rules, { ruleName: item?.name });
        rule.push(temp_rule);
      });

      forEach(task?.ruleName, (item: any) => {
        forEach(item?.children, (deps_tasks: any) => {
          find(rules, { ruleName: get(item, "name") });
          if (get(find(rules, { ruleName: get(item, "name") }), "properties[0].property") === "display") {
            dependents.push({
              id: parseInt(deps_tasks),
              display: false,
              parent: task.subtaskId,
              rule: item.name,
            });
          }
        });
      });

      if (rule?.length > 0 && rule[0]?.operation === EQUALS) {
        forEach(task?.ruleName, (item: any) => {
          const temp_rule = find(rule, { ruleName: item?.name });
          if (caseLessComparision(temp_rule?.value, task?.value)) {
            forEach(item?.children, (populated_dependent_tasks: any) => {
              const searched_depSubTask = find(dependents, { id: parseInt(populated_dependent_tasks) });
              if (searched_depSubTask) {
                const displayRule = find(temp_rule?.properties, { property: "display" });
                searched_depSubTask.display = displayRule?.value ?? searched_depSubTask.display;
              }
            });
          }
        });
      }

      if (rule?.length > 0 && rule[0]?.operation === NOT_EQUALS) {
        forEach(task?.ruleName, (item: any) => {
          const temp_rule = find(rule, { ruleName: item?.name });
          if (task?.value !== "" && task?.value !== null) {
            forEach(item?.children, (populated_dependent_tasks: any) => {
              const searched_depSubTask = find(dependents, { id: parseInt(populated_dependent_tasks) });
              if (searched_depSubTask) {
                const displayRule = find(temp_rule?.properties, { property: "display" });
                searched_depSubTask.display = displayRule?.value ?? searched_depSubTask.display;
              }
            });
          }
        });
      }

      if (rule?.length > 0 && rule[0]?.operation === 'contains') {
        forEach(task?.ruleName, (item: any) => {
          const temp_rule = find(rule, { ruleName: item?.name });
          if (task?.value && task?.value.toLowerCase().includes(temp_rule?.value.toLowerCase())) {
            forEach(item?.children, (populated_dependent_tasks: any) => {
              const searched_depSubTask = find(dependents, { id: parseInt(populated_dependent_tasks) });
              if (searched_depSubTask) {
                const displayRule = find(temp_rule?.properties, { property: "display" });
                searched_depSubTask.display = displayRule?.value ?? searched_depSubTask.display;
              }
            });
          }
        });
      }

      dependantItems = [...dependantItems, ...dependents];
    });
    setDependantSubTasks(dependantItems);
  }, []);

  const dependantIds = map(dependantSubTasks, (obj) => {
    return obj.id;
  });

  return (
    <>
      <div className="ui-care-plan-report__program__tasks__body__task__header">
        <div className="ui-care-plan-report__program__tasks__body__task__description">{taskItem.description}</div>
        <div className={`ui-care-plan-report__program__tasks__body__status ui-care-plan-report__program__tasks__body__status--${taskItem.status}`}>
          {taskItem.status === "InProgress" ? "In Progress" : taskItem.status}
        </div>
      </div>
      <ProgramDataView {...({displayType : get(taskItem , 'displayType' , '') , dependantIds , dependantSubTasks , taskItem})}/>
    </>
  );
};

const CareTeam = ({ data }: any) => {
  return (
    <div className={`ui-care-plan-report__program__care-team ${data?.styleClass ? data?.styleClass : ""}`}>
      <div className="ui-care-plan-report__program__care-team__details">
        {isEmpty(data) ? (
          <DataNotFound message={NO_DATA_FOUND}/>
        ) : (
          <>
            <div className="ui-care-plan-report__program__care-team__details__name">{data.name}</div>
            <div className="ui-care-plan-report__program__care-team__details__designation">{data.designation}</div>
          </>
        )}
      </div>
    </div>
  );
};

const ProgramNotes = ({ data }: any) => {
  return (
    <div className="ui-care-plan-report__program__notes">
      <div className="ui-care-plan-report__program__notes__heading">Notes:</div>
      {isEmpty(data) ? <DataNotFound message={NO_DATA_FOUND}/> : <></>}
      {map(data, (item, index) => {
        return (
          <div className="ui-care-plan-report__program__notes__note" key={index}>
            <div className="ui-care-plan-report__program__notes__note__left">
              <div className="ui-care-plan-report__program__notes__note__left__description" dangerouslySetInnerHTML={{ __html: item.description }}></div>
              <div className="ui-care-plan-report__program__notes__note__left__footer">
                {item.createdBy ? (
                  <>
                    <span className="ui-care-plan-report__program__notes__note__left__footer__createdBy">{item.createdBy}</span>
                    <div className="ui-care-plan-report__program__notes__note__left__footer__partition">|</div>
                  </>
                ) : null}
                <span className="ui-care-plan-report__program__notes__note__left__footer__createdOn">
                  {moment(item.createdOn)?.format(dateFormatConfig.get(getClientName())["dateTimeColumnFilterDisplayFormatWithTime"])}
                </span>
              </div>
            </div>
            {/* <div className="ui-care-plan-report__program__notes__note__right">{item.taskId}</div> */}
          </div>
        );
      })}
    </div>
  );
};

export const NoDataFound = () => {
  return (
    <div className="care-report-no-data-found">
      <div className="care-report-no-data-found__icon">
        <Icon name="noDataFound" />
      </div>
      <div className="care-report-no-data-found__message">No Data Found</div>
    </div>
  );
};

function ProgramDataView({displayType , dependantIds , dependantSubTasks , taskItem} : any) {

  if(displayType === 'table') {
    return <TableView {...({dependantIds , dependantSubTasks , taskItem})}/>
  }

  return <ListView {...({dependantIds , dependantSubTasks , taskItem})}/>
}

function TableView({taskItem} : any) {

  const {tableheaders , groupedData} = useMemo(() => {

    const subtasks = sortBy(get(taskItem, "subtasks", []), 'subtaskId');
    
    const _tableheaders: string[] = uniq(flatMap(subtasks, o => Object.keys({ [o.description]: o.value })));

    const dataObj : any = {};

    _tableheaders.forEach((th) => {
      dataObj[th] = [];
    })

    let groupedData : any = groupBy(subtasks, 'recordCreatedDate');

    groupedData = Object.entries(groupedData);

    groupedData.sort((a : any, b : any) => moment(get(b , '[1][0].value')).diff(moment(get(a ,'[1][0].value'))));

    return { tableData: dataObj, tableheaders: _tableheaders , groupedData};
  }, [taskItem]);
 

  return (
    <div className="ui-care-plan-report__program__tasks__body">
      <div className="ui-care-plan-report__program__tasks__body_table--header">
        {tableheaders.map((th: any, index: number) => {
          return (
            <span key={index} className="ui-care-plan-report__program__tasks__body_table--heading">
              {th}
            </span>
          );
        })}
      </div>
      <div className="ui-care-plan-report__program__tasks__body_table--body">
        {groupedData.map((data: any) => {
          const [key, outreach] = data;
          return <TableRow key={key} outreach={outreach} tableheaders={tableheaders}/>;
        })}
      </div>
    </div>
  );
}

function TableRow({outreach , tableheaders , key} : any) {
  
  return (
    <div key={key} className="ui-care-plan-report__program__tasks__body_table--row">
      {
        tableheaders.map((th : any, index : number) => {

          let value = get(find(outreach , (o) => o.description === th) ,'value' , '') || '';

          let cellClassName = lowerCase(th).split(" ").join("");

          if (cellClassName === "outreachtimestamp") {
            value = moment(value).isValid() ? moment(value).format(dateFormatConfig.get(getClientName())["dateTimeColumnFilterDisplayFormatWithTime"]) : value;
          }

          if (cellClassName === "outcomeofoutreach") {
            cellClassName = `${cellClassName} ${positiveOutComeOfOutreach.includes(value.toLowerCase()) ? "green" : negativeOutcomeOfOutreach.includes(value.toLowerCase()) ? "red" : "green"}`;
          }

          return <div key={index} className={`ui-care-plan-report__program__tasks__body_table--cell ${cellClassName}`}>{value}</div>
        })
      }
    </div>
  )
}

function ListView({dependantIds , dependantSubTasks , taskItem}: any) {
  
  return (
    <div className="ui-care-plan-report__program__tasks__body">
        {map(
          filter(taskItem.subtasks, (item) => {
            return !includes(dependantIds, item.subtaskId) || !isEmpty(find(dependantSubTasks, { display: true, id: item.subtaskId }));
          }),
          (subTask) => {
            return (
              <div className="ui-care-plan-report__program__tasks__body__subTask-container" key={subTask.subtaskId}>
                <div
                  className={`ui-care-plan-report__program__tasks__body__subTask-container__bullet-container ${
                    includes(dependantIds, subTask.subtaskId) ? "ui-care-plan-report__program__tasks__body__subTask-container__bullet-container--extra" : ""
                  }`}
                >
                  <div className="ui-care-plan-report__program__tasks__body__subTask-container__bullet-container__bullet" />
                </div>
                <div className={`ui-care-plan-report__program__tasks__body__subTask-container__subtasks__description`}>
                  {subTask.description}
                  {subTask.value && <span className="ui-care-plan-report__program__tasks__body__subTask-container__subtasks__description__separator">-</span>}
                  {subTask.value && (
                    <span className="ui-care-plan-report__program__tasks__body__subTask-container__subtasks__description__value">
                      {subTask.type === SingleDate
                        ? moment(subTask.value)?.format(dateFormatConfig.get(getClientName())["defaultDateFormat"])
                        : subTask.type === ActionDateTime && (subTask?.description?.trim() === "Admission Date" || subTask?.description?.trim() === "Discharge Date")
                        ? !isEmpty(subTask.value?.trim())
                          ? moment(subTask.value)?.utc()?.format(dateFormatConfig.get(getClientName())["dateTimeColumnFilterDisplayFormatWithTime"])
                          : ""
                        : subTask.type === ActionDateTime
                        ? moment(subTask.value)?.format(dateFormatConfig.get(getClientName())["dateTimeColumnFilterDisplayFormatWithTime"])
                        : subTask.value?.replaceAll("$|", ", ")}
                    </span>
                  )}
                </div>
              </div>
            );
          }
        )}
      </div>
  );
}

export default ProgramPage;
